import axios from 'axios';
import { getAuthHeaders } from '../utils/authUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Service for managing folder permissions
 */
class FolderPermissionService {
    constructor() {
        this.baseUrl = `${API_BASE_URL}/api/v2/permissions`;
    }

    /**
     * Get permissions for a specific folder
     * @param {string} folderPath - Path to the folder
     * @returns {Promise<Object>} Folder permissions
     */
    async getFolderPermissions(folderPath) {
        try {
            const headers = getAuthHeaders(true);
            const response = await axios.get(`${this.baseUrl}/folder/permissions?path=${folderPath}`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching folder permissions:', error);
            throw error;
        }
    }

    /**
     * Set user permission for a folder
     * @param {string} folderPath - Path to the folder
     * @param {string} userEmail - User email
     * @param {string} permission - Permission level (READ, WRITE, ADMIN)
     * @returns {Promise<Object>} Updated permissions
     */
    async setUserPermission(folderPath, userEmail, permission) {
        try {
            const headers = getAuthHeaders(true);

            // Use PUT method to match backend controller
            const response = await axios.put(
                `${this.baseUrl}/folder/user-permission?path=${folderPath}&userEmail=${userEmail}&accessLevel=${permission}`,
                {}, // Empty body since we're sending everything as query params
                { headers },
            );
            return response.data;
        } catch (error) {
            console.error('Error setting user permission:', error);
            throw error;
        }
    }

    /**
     * Set group permission for a folder
     * @param {string} folderPath - Path to the folder
     * @param {string} groupId - Group ID
     * @param {string} permission - Permission level (READ, WRITE, ADMIN)
     * @returns {Promise<Object>} Updated permissions
     */
    async setGroupPermission(folderPath, groupId, permission) {
        try {
            const headers = getAuthHeaders(true);
            const response = await axios.put(
                `${this.baseUrl}/folder/group-permission?path=${folderPath}&groupId=${groupId}&accessLevel=${permission}`,
                {},
                { headers },
            );
            return response.data;
        } catch (error) {
            console.error('Error setting group permission:', error);
            throw error;
        }
    }

    /**
     * Remove user permission from a folder
     * @param {string} folderPath - Path to the folder
     * @param {string} userEmail - User email
     * @returns {Promise<Object>} Updated permissions
     */
    async removeUserPermission(folderPath, userEmail) {
        try {
            const headers = getAuthHeaders(true);

            const response = await axios.delete(
                `${this.baseUrl}/folder/user-permission?path=${folderPath}&userEmail=${userEmail}`,
                {
                    headers,
                },
            );
            return response.data;
        } catch (error) {
            console.error('Error removing user permission:', error);
            throw error;
        }
    }

    /**
     * Remove group permission from a folder
     * @param {string} folderPath - Path to the folder
     * @param {string} groupId - Group ID
     * @returns {Promise<Object>} Updated permissions
     */
    async removeGroupPermission(folderPath, groupId) {
        try {
            const headers = getAuthHeaders(true);
            const response = await axios.delete(
                `${this.baseUrl}/folder/group-permission?path=${folderPath}&groupId=${groupId}`,
                { headers },
            );
            return response.data;
        } catch (error) {
            console.error('Error removing group permission:', error);
            throw error;
        }
    }

    /**
     * Toggle inherit permissions from parent
     * @param {string} folderPath - Path to the folder
     * @param {boolean} inherit - Whether to inherit permissions
     * @returns {Promise<Object>} Updated permissions
     */
    async setInheritPermissions(folderPath, inherit) {
        try {
            const headers = getAuthHeaders(true);
            const response = await axios.put(
                `${this.baseUrl}/folder/inheritance?path=${folderPath}&inherit=${inherit}`,
                {},
                { headers },
            );
            return response.data;
        } catch (error) {
            console.error('Error setting inherit permissions:', error);
            throw error;
        }
    }

    /**
     * Migrate permissions by creating standard groups and applying permissions to all folders
     * @returns {Promise<Object>} The migration result
     */
    migratePermissions() {
        const headers = getAuthHeaders(true);
        return axios
            .post(`${this.baseUrl}/migrate`, {}, { headers })
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                console.error('Error migrating permissions:', error);
                throw error;
            });
    }

    /**
     * Explains why a user has access to a specific folder
     * @param {string} folderPath - Path of the folder
     * @param {string} username - Username or email of the user
     * @returns {Promise<Object>} - Access explanation data
     */
    async explainUserAccess(folderPath, username) {
        const headers = getAuthHeaders(true);
        try {
            const response = await axios.get(
                `${this.baseUrl}/folder/access-explanation?path=${folderPath}&targetEmail=${username}`,
                { headers },
            );
            return response.data;
        } catch (error) {
            console.error('Error explaining user access:', error);
            throw error;
        }
    }
}

const folderPermissionService = new FolderPermissionService();
export default folderPermissionService;
